import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 20;

/**
 * Searchable, server-paged picker for long lists (Aug 2026).
 *
 * Replaces plain `<Select>` dropdowns that were fed a single truncated request:
 * anything past the cap was unreachable and there was no way to search for it.
 *
 * - search runs on the SERVER (`?search=`) across every record, debounced 300ms
 * - loads 20 at a time and fetches the next page when the list is scrolled
 * - keeps the chosen option visible even when it isn't on the first page
 * - shows "Showing X of Y" so a truncated list is never invisible again
 *
 * Endpoint contract: `{ items[], total, has_more }` (also accepts `data[]`).
 */
export default function AsyncSearchSelect({
  endpoint,
  params = {},
  value,
  onChange,
  getOptionLabel,
  getOptionValue = (o) => o.id,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches found',
  disabled = false,
  testId = 'async-search-select',
  selectedLabel,
  renderOption,
  clearLabel,
  onOptionsLoaded,
  filterOption,
}) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const sentinel = useRef(null);
  const listRef = useRef(null);
  const onOptionsLoadedRef = useRef(onOptionsLoaded);
  onOptionsLoadedRef.current = onOptionsLoaded;
  const paramKey = JSON.stringify(params);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  const load = useCallback(async (nextPage, replace) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(endpoint, {
        params: { ...params, page: nextPage, pageSize: PAGE_SIZE, ...(debounced ? { search: debounced } : {}) },
      });
      const body = res.data || {};
      const rows = body.items || body.data || [];
      setOptions((prev) => (replace ? rows : [...prev, ...rows]));
      setTotal(typeof body.total === 'number' ? body.total : rows.length);
      setHasMore(body.has_more === true);
      setPage(nextPage);
    } catch (e) {
      setError('Could not load options. Please try again.');
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [endpoint, paramKey, debounced]); // eslint-disable-line react-hooks/exhaustive-deps

  // First page on open, and again whenever the search term or filters change.
  useEffect(() => {
    if (!open) return;
    load(1, true);
  }, [open, debounced, paramKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Let the parent see what has been loaded (used for availability badges).
  useEffect(() => {
    if (onOptionsLoadedRef.current) onOptionsLoadedRef.current(options);
  }, [options]);

  // Next page once the list is scrolled near the bottom.
  const handleScroll = useCallback((e) => {
    if (!hasMore || loading) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) load(page + 1, false);
  }, [hasMore, loading, page, load]);

  // Radix Dialog locks page scroll and swallows wheel events on portalled
  // popovers, so drive the list manually while a lock is active.
  const handleWheel = useCallback((e) => {
    if (!document.body.hasAttribute('data-scroll-locked')) return;
    const el = e.currentTarget;
    el.scrollTop += e.deltaY;
  }, []);

  const match = value ? options.find((o) => String(getOptionValue(o)) === String(value)) : null;
  const currentLabel = selectedLabel
    || (match ? getOptionLabel(match) : '')
    || (picked && String(getOptionValue(picked)) === String(value) ? getOptionLabel(picked) : '');

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTerm(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', !currentLabel && 'text-muted-foreground')}
          data-testid={testId}
        >
          <span className="truncate">{currentLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={term}
            onValueChange={setTerm}
            data-testid={`${testId}-search`}
          />
          <CommandList
            ref={listRef}
            onScroll={handleScroll}
            onWheel={handleWheel}
            className="max-h-72 overflow-y-auto overscroll-contain"
            data-testid={`${testId}-list`}
          >
            {error && (
              <div className="px-3 py-4 text-sm text-destructive" data-testid={`${testId}-error`}>{error}</div>
            )}
            {!error && !loading && options.length === 0 && (
              <CommandEmpty data-testid={`${testId}-empty`}>{emptyText}</CommandEmpty>
            )}
            {clearLabel && (
              <CommandItem
                value="__clear__"
                onSelect={() => { setPicked(null); onChange('', null); setOpen(false); setTerm(''); }}
                data-testid={`${testId}-clear`}
              >
                <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                <span className="text-muted-foreground">{clearLabel}</span>
              </CommandItem>
            )}
            {(filterOption ? options.filter(filterOption) : options).map((option) => {
              const optValue = String(getOptionValue(option));
              return (
                <CommandItem
                  key={optValue}
                  value={optValue}
                  onSelect={() => { setPicked(option); onChange(optValue, option); setOpen(false); setTerm(''); }}
                  data-testid={`${testId}-option-${optValue}`}
                >
                  <Check className={cn('mr-2 h-4 w-4', String(value) === optValue ? 'opacity-100' : 'opacity-0')} />
                  {renderOption ? renderOption(option) : getOptionLabel(option)}
                </CommandItem>
              );
            })}
            <div ref={sentinel} />
            {!loading && hasMore && (
              <button
                type="button"
                onClick={() => load(page + 1, false)}
                className="w-full px-3 py-2 text-xs font-medium text-primary hover:bg-accent"
                data-testid={`${testId}-load-more`}
              >
                Load {PAGE_SIZE} more
              </button>
            )}
            {loading && (
              <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground" data-testid={`${testId}-loading`}>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            )}
            {!loading && total > 0 && (
              <div className="px-3 py-2 text-[11px] text-muted-foreground border-t" data-testid={`${testId}-count`}>
                Showing {options.length} of {total}
                {hasMore ? ' — scroll for more' : ''}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
